import { useEffect, useMemo, useState } from "react";
import { GetCookie } from "../../exports/CookieManagement";
import { getDeviceSize } from "../../exports/InteractiveUtils";
import ServerRequest from "../../exports/ServerRequest";
import CustomSelect from "../Body/CustomChartComponents/CustomSelect/CustomSelect";
import CustomSelectWithRender from "../Body/CustomChartComponents/CustomSelectWithRender/CustomSelectWithRender";
import PerformanceLineChart from "../Body/PerformanceChart";
import Pulse from "../Loader/Pulse";
import { monthData, totalPortfolioCategories } from "./dummyData";

const DEVICE_SIZE = getDeviceSize();

const RANGE_ARRAY = [
  { title: "1M", value: "1M" },
  { title: "3M", value: "3M" },
  { title: "6M", value: "6M" },
  { title: "1Y", value: "1Y" },
  { title: "YTD", value: "YTD" },
];

const Performance = ({ price = 664, change = 52, changePercent = 2.6 }) => {
  const [benchmarkData, setBenchmarkData] = useState([]);
  const [selectedBenchmark, setSelectedBenchmark] = useState(null);
  const [totalPortfolio, setTotalPortfolio] = useState(
    totalPortfolioCategories[0]
  );
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("1Y");

  const account_token = GetCookie("token");

  useEffect(() => {
    const fetchBenchmarkData = async () => {
      setLoading(true);
      try {
        const res = await ServerRequest({
          url: `/mf/benchmark/get?token=${account_token}`,
          method: "get",
        });

        const bData = res.data.map((i) => i.benchmark_common_name);
        setBenchmarkData(bData);

        if (bData.length > 0) {
          setSelectedBenchmark(bData[0]);
        }
      } catch (error) {
        throw error;
      } finally {
        setLoading(false);
      }
    };
    fetchBenchmarkData();
  }, [account_token]);

  const seriesConfig = useMemo(
    () => [
      {
        name: selectedBenchmark,
        yAccessor: (d) => d.nifty,
        stroke: "#C4C4C4",
        strokeWidth: 2,
        type: "Line",
      },
      {
        name: totalPortfolio.name,
        yAccessor: (d) => d.portfolio,
        stroke: "#3179f5",
        strokeWidth: 2.5,
        type: "Line",
        isPrimary: true,
      },
    ],
    [selectedBenchmark, totalPortfolio]
  );
  console.log(seriesConfig);

  const yExtentsCalculator = (d) => {
    return seriesConfig.map((series) => series.yAccessor(d));
  };

  useEffect(() => {
    const generateData = () => {
      const data = monthData.map((d, i) => ({
        date: new Date(2024, i, 1),
        portfolio: d.portfolio + (Math.random() - 0.5) * 5,
        nifty: d.nifty + (Math.random() - 0.5) * 5,
        close: d.portfolio,
      }));
      setChartData(data);
      setLoading(false);
    };
    generateData();
  }, [range, selectedBenchmark, totalPortfolio]);

  if (loading) {
    return (
      <div className="mf-loading">
        <Pulse />
{/* <p>Loading Portfolio data...</p> */}
      </div>
    );
  }

  return (
    <div className="mf-wrapper">
      <div className="mf-performance-container">
        <div className="selector-direction">
          <CustomSelect
            heading="Nifty50"
            defaultIndex={benchmarkData.indexOf(selectedBenchmark)}
            options={benchmarkData}
            width={240}
            height={40}
            placeholder="Nifty50"
            onTypeChange={(value) => {
              setSelectedBenchmark(value);
            }}
          />
          <CustomSelectWithRender
            heading="Total Portfolio"
            defaultIndex={totalPortfolioCategories.indexOf(totalPortfolio)}
            options={totalPortfolioCategories}
            renderSelected={(item) => item.name}
            renderOption={(item) => (
              <div className="portfolio-item">
                <div className="portfolio-item-header">
                  <span className="portfolio-name">{item.name}</span>
                </div>
                <div className="portfolio-item-details">
                  <div className="folio-info">Folio: {item.folio}</div>
                  <div className="portfolio-value">
                    Portfolio Value: Rs. {item.value.toLocaleString("en-IN")}
                  </div>
                </div>
              </div>
            )}
            onChange={(selected) => setTotalPortfolio(selected)}
          />
        </div>
        <div className="performance-chart-metrics-container">
          <div className="performance-chart-container">
            <div className="performance-chart-header">
              <div className="portfolio-info">
                <div className="portfolio-details">
                  <p className="stock__name__code">
                    <span>{totalPortfolio.name}</span>
                  </p>
                  <div className="stock__name__code">
                    <span>{selectedBenchmark}</span>
                  </div>
                </div>
                <div className="portfolio-vs-nifty-change">
                  <div className="stock__price">
                    <div className="price">{price}</div>
                    <div className="performance-change">
                      <div
                        className={`performance-percentage ${
                          change > 0 ? "positive" : "negative"
                        }`}
                      >
                        {change}
                      </div>
                      <div
                        className={`performance-percentage ${
                          changePercent > 0 ? "positive" : "negative"
                        }`}
                      >
                        ({changePercent}%)
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <PerformanceLineChart
              data={chartData}
              width={DEVICE_SIZE === "S" ? 350 : 1050}
              height={DEVICE_SIZE === "S" ? 412 : 350}
              ratio={3}
              seriesConfig={seriesConfig}
              yExtentsCalculator={yExtentsCalculator}
            />

            <div className="mf-chart-range">
              {RANGE_ARRAY.map((r) => (
                <RangeBox
                  key={r.title}
                  range={r}
                  active={range === r.value}
                  setRange={setRange}
                  disabled={loading}
                />
              ))}
            </div>
          </div>

          <PerformanceMetrics />
        </div>
      </div>
    </div>
  );
};

function RangeBox({ range, active, setRange, disabled }) {
  return (
    <div
      className={`mf-range-value ${active ? "active__range" : ""} ${
        disabled ? "disabled" : ""
      }`}
      onClick={() => !disabled && setRange(range.value)}
    >
      {range.title}
    </div>
  );
}

export default Performance;

const metrics = [
  {
    label: "Mtd",
    value: "12.3%",
    subValue: "10.08%",
  },
  {
    label: "1 Month",
    value: "12.3%",
    subValue: "10.08%",
  },
  {
    label: "3 Month",
    value: "12.3%",
    subValue: "10.08%",
  },
  {
    label: "6 Month",
    value: "12.3%",
    subValue: "10.08%",
  },
  {
    label: "1 Year",
    value: "12.3%",
    subValue: "10.08%",
  },

  {
    label: "Annual Return",
    value: "12.3%",
    subValue: "10.08%",
  },
  {
    label: "Annual Risk",
    value: "12.3%",
    subValue: "10.08%",
  },
  {
    label: "Sharp Ratio",
    value: "12.3%",
    subValue: "10.08%",
  },
];

const PerformanceMetrics = () => {
  const isSmallDevice = DEVICE_SIZE === "S";

  if (isSmallDevice) {
    const rows = [];
    for (let i = 0; i < metrics.length; i += 2) {
      rows.push(metrics.slice(i, i + 2));
    }

    return (
      <div className="performance-metrics">
        {rows.map((row, rowIdx) => (
          <div
            key={rowIdx}
            className="performance-metrics-row"
            style={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            {row.map((metric, colIdx) => (
              <div
                key={colIdx}
                className="metric-card"
                style={{
                  textAlign: colIdx === 0 ? "left" : "right",
                  padding: "0 8px",
                }}
              >
                <div className="metric-label" style={{ fontWeight: "bold" }}>
                  {metric.label}
                </div>
                <div className="metric-value" style={{ display: "block" }}>
                  {metric.value}
                </div>
                {metric.subValue && (
                  <div className="metric-subvalue">{metric.subValue}</div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="performance-metrics">
      <div className="performance-metrics-row">
        {metrics.map((metric, idx) => (
          <div key={idx} className="metric-card">
            <div className="metric-label">{metric.label}</div>
            <div className="metric-value">{metric.value}</div>
            {metric.subValue && (
              <div className="metric-subvalue">{metric.subValue}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
